/* eslint-disable no-console */
import { MouseEventHandler, createElement } from 'react';
import { HOC } from 'src/shared/componentBuilder';
import { ClientProfileViewModel } from '@common/model/client';
import { useClientProfile } from 'src/entities/client';
import { useCheckPatronageCompleted } from './useCheckPatronageCompleted';
import { useShowPatronageHints } from './useShowPatronageHints';

interface CheckPatronageProps {
  responder?: ClientProfileViewModel;
  onClick?: MouseEventHandler<HTMLElement>;
}

/**
 * Higher-order component that enhances a given component with patronage hint logic.
 *
 * When the wrapped component is clicked, it checks whether patronage hints should be shown
 * based on the responder's profile and completion status. If hints are applicable, it triggers
 * a hint display mechanism and prevents further click propagation. Otherwise, it delegates
 * the click event to the original handler.
 *
 * Props:
 * - `responder` (optional): A specific client profile to evaluate. Defaults to the current client profile.
 * - `onClick` (optional): Original click handler to invoke if patronage checks pass.
 */
export const withPatronageHintsClick: HOC<CheckPatronageProps> = (component) => (props) => {
  const clientProfile = useClientProfile();
  const showPatronageHints = useShowPatronageHints();
  const checkPatronageHints = useCheckPatronageCompleted();

  const onClick: MouseEventHandler<HTMLElement> = async (event) => {
    console.debug(`[PatronageHints]: Starting the checking process...`);

    const responder = props.responder ?? clientProfile;
    const checkedPatronage = await checkPatronageHints(responder);

    if (!(responder?.mainResumeIsPublished || checkedPatronage)) {
      showPatronageHints();
      console.debug(`[PatronageHints]: Patronage has no hints`);
      return Promise.reject(new Error('Patronage has no hints'));
    }

    console.debug(`[PatronageHints]: Checking process has been finished successfully!`);
    props.onClick?.(event);
  };

  return createElement(component, { ...props, onClick });
};
